/** Stable camera-distance identifiers shared with render jobs. */
export const CAMERA_DISTANCE_IDS = ["wide", "standard", "close", "x-close", "xx-close", "telephoto-800"] as const;

/** Supported distance between the moving camera and route subject. */
export type CameraDistanceId = (typeof CAMERA_DISTANCE_IDS)[number];

/** Reference distance used when no explicit selection is present. */
export const DEFAULT_CAMERA_DISTANCE_ID: CameraDistanceId = "standard";

/** Lens-style framing names; zoom offsets retain the existing camera geometry. */
export interface CameraDistanceDefinition {
  readonly description: string;
  readonly id: CameraDistanceId;
  readonly label: string;
  readonly zoomOffset: number;
}

/** Bounded distance choices for reproducible preview and video composition. */
export const CAMERA_DISTANCES: readonly CameraDistanceDefinition[] = [
  {
    description: "An expansive view of the trace and surrounding terrain.",
    id: "wide",
    label: "24mm",
    zoomOffset: -0.75,
  },
  {
    description: "A balanced view of the trace within its surroundings.",
    id: "standard",
    label: "50mm",
    zoomOffset: 0,
  },
  {
    description: "A tighter view of the trace and nearby terrain.",
    id: "close",
    label: "100mm",
    zoomOffset: 0.9,
  },
  {
    description: "A tight view that emphasizes local terrain detail.",
    id: "x-close",
    label: "200mm",
    zoomOffset: 1.8,
  },
  {
    description: "Very tight framing of the trace and nearby terrain.",
    id: "xx-close",
    label: "400mm",
    zoomOffset: 2.7,
  },
  {
    description: "Twice the magnification of 400mm framing.",
    id: "telephoto-800",
    label: "800mm",
    zoomOffset: 3.7,
  },
] as const;

/**
 * Tests whether an unknown string names a supported camera distance.
 *
 * @param candidate - Candidate distance identifier.
 * @returns Whether the identifier is present in the stable registry.
 */
export function isCameraDistanceId(candidate: string): candidate is CameraDistanceId {
  return CAMERA_DISTANCE_IDS.some((id) => id === candidate);
}

/**
 * Resolves a camera-distance definition from its stable identifier.
 *
 * @param id - Supported camera-distance identifier.
 * @returns Matching immutable camera-distance metadata.
 */
export function resolveCameraDistance(id: CameraDistanceId): CameraDistanceDefinition {
  return CAMERA_DISTANCES.find((distance) => distance.id === id)!;
}
